/*!

=========================================================
* Argon Dashboard React - v1.2.4
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from "react";
import { useLocation, Route, Routes, Navigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Carousel,
  CarouselItem,
  CarouselControl,
  CarouselIndicators,
  Modal,
  ModalBody,
} from "reactstrap";

import AuthNavbar from "components/Navbars/AuthNavbar.js";
import AuthFooter from "components/Footers/AuthFooter.js";

import routes from "routes.js";

const APP_BASE_RAW = process.env.PUBLIC_URL || "";
const APP_BASE =
  APP_BASE_RAW && APP_BASE_RAW !== "/" ? APP_BASE_RAW.replace(/\/+$/, "") : "";

const AUTH_BANNER_ITEMS = [
  {
    src: `${APP_BASE}/banners/banner-1.jpeg`,
    altText: "Karadeniz Destanları - Banner 1",
    key: "banner-1",
  },
  {
    src: `${APP_BASE}/banners/banner-2.jpeg`,
    altText: "Karadeniz Destanları - Banner 2",
    key: "banner-2",
  },
  {
    src: `${APP_BASE}/banners/banner-3.jpeg`,
    altText: "Karadeniz Destanları - Banner 3",
    key: "banner-3",
  },
  {
    src: `${APP_BASE}/banners/banner-4.jpeg`,
    altText: "Karadeniz Destanları - Banner 4",
    key: "banner-4",
  },
  {
    src: `${APP_BASE}/banners/banner-5.jpeg`,
    altText: "Karadeniz Destanları - Banner 5",
    key: "banner-5",
  },
  {
    src: `${APP_BASE}/banners/banner-6.jpeg`,
    altText: "Karadeniz Destanları - Banner 6",
    key: "banner-6",
  },
  {
    src: `${APP_BASE}/banners/banner-7.jpeg`,
    altText: "Karadeniz Destanları - Banner 7",
    key: "banner-7",
  },
  {
    src: `${APP_BASE}/banners/banner-8.jpeg`,
    altText: "Karadeniz Destanları - Banner 8",
    key: "banner-8",
  },
];

const Auth = (props) => {
  const mainContent = React.useRef(null);
  const location = useLocation();

  const [isBannerPreviewOpen, setIsBannerPreviewOpen] = React.useState(false);
  const [bannerPreviewItem, setBannerPreviewItem] = React.useState(AUTH_BANNER_ITEMS[0]);

  const [activeBannerIndex, setActiveBannerIndex] = React.useState(0);
  const [isBannerAnimating, setIsBannerAnimating] = React.useState(false);

  const openBannerPreview = (item) => {
    setBannerPreviewItem(item);
    setIsBannerPreviewOpen(true);
  };

  const nextBanner = () => {
    if (isBannerAnimating) return;
    setActiveBannerIndex((prev) =>
      prev === AUTH_BANNER_ITEMS.length - 1 ? 0 : prev + 1
    );
  };

  const prevBanner = () => {
    if (isBannerAnimating) return;
    setActiveBannerIndex((prev) =>
      prev === 0 ? AUTH_BANNER_ITEMS.length - 1 : prev - 1
    );
  };

  const goToBanner = (index) => {
    if (isBannerAnimating) return;
    setActiveBannerIndex(index);
  };

  React.useEffect(() => {
    document.body.classList.add("bg-default");
    return () => {
      document.body.classList.remove("bg-default");
    };
  }, []);
  React.useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    mainContent.current.scrollTop = 0;
  }, [location]);

  const getRoutes = (routes) => {
    return routes.map((prop, key) => {
      if (prop.layout === "/auth") {
        const routePath = prop.path?.startsWith("/") ? prop.path.slice(1) : prop.path;
        return (
          <Route path={routePath} element={prop.component} key={key} />
        );
      } else {
        return null;
      }
    });
  };

  return (
    <>
      <div className="main-content" ref={mainContent}>
        <AuthNavbar />
        <div className="header bg-gradient-info py-7 py-lg-8">
          <Container>
            <div className="header-body text-center mb-7">
              <Row className="justify-content-center">
                <Col xs="12" sm="10" md="6" lg="5">
                  <h1 className="text-white">Hoş geldiniz!</h1>
                  <p className="text-lead text-white">
                    Giriş yapmak veya yeni bir hesap oluşturmak için aşağıdaki
                    formları kullanabilirsiniz.
                  </p>
                </Col>
              </Row>

              <Row className="justify-content-center mt-4">
                <Col xs="12" lg="10" xl="8">
                  <Carousel
                    activeIndex={activeBannerIndex}
                    next={nextBanner}
                    previous={prevBanner}
                    interval={5000}
                  >
                    <CarouselIndicators
                      items={AUTH_BANNER_ITEMS}
                      activeIndex={activeBannerIndex}
                      onClickHandler={goToBanner}
                    />

                    {AUTH_BANNER_ITEMS.map((item) => (
                      <CarouselItem
                        key={item.key}
                        onExiting={() => setIsBannerAnimating(true)}
                        onExited={() => setIsBannerAnimating(false)}
                      >
                        <div
                          className="rounded"
                          style={{
                            width: "100%",
                            maxWidth: 1500,
                            margin: "0 auto",
                            aspectRatio: "2 / 1",
                            overflow: "hidden",
                            cursor: "pointer",
                          }}
                          role="button"
                          tabIndex={0}
                          aria-label={`${item.altText} (büyüt)`}
                          onClick={() => openBannerPreview(item)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") openBannerPreview(item);
                          }}
                        >
                          <img
                            src={item.src}
                            alt={item.altText}
                            className="d-block w-100"
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        </div>
                      </CarouselItem>
                    ))}

                    <CarouselControl
                      direction="prev"
                      directionText="Previous"
                      onClickHandler={prevBanner}
                    />
                    <CarouselControl
                      direction="next"
                      directionText="Next"
                      onClickHandler={nextBanner}
                    />
                  </Carousel>
                </Col>
              </Row>
            </div>
          </Container>
          <div className="separator separator-bottom separator-skew zindex-100">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="none"
              version="1.1"
              viewBox="0 0 2560 100"
              x="0"
              y="0"
            >
              <polygon
                className="fill-default"
                points="2560 0 2560 100 0 100"
              />
            </svg>
          </div>
        </div>
        {}
        <Container className="mt--8 pb-5">
          <Row className="justify-content-center">
            <Routes>
              {getRoutes(routes)}
              <Route
                path="*"
                element={<Navigate to={`${APP_BASE}/auth/login`} replace />}
              />
            </Routes>
          </Row>
        </Container>
      </div>
      <AuthFooter />

      <Modal
        isOpen={isBannerPreviewOpen}
        toggle={() => setIsBannerPreviewOpen(false)}
        size="lg"
        centered
      >
        <ModalBody className="p-0" style={{ position: "relative" }}>
          <button
            type="button"
            className="close"
            aria-label="Kapat"
            onClick={() => setIsBannerPreviewOpen(false)}
            style={{ position: "absolute", top: 8, right: 12, zIndex: 2 }}
          >
            <span aria-hidden="true">×</span>
          </button>
          <img
            src={bannerPreviewItem?.src}
            alt={bannerPreviewItem?.altText || "Banner"}
            className="img-fluid"
            style={{ width: "100%", maxHeight: "80vh", objectFit: "contain" }}
          />
        </ModalBody>
      </Modal>
    </>
  );
};

export default Auth;
